/**
 * ImportSearchLinks — the linked search an import belongs to, rendered as the
 * PAIR a recruiter needs: the Thrive id (out to the Thrive tenant) and the search
 * name (in to our own Search Detail page).
 *
 * FIVE callers, SEVEN surfaces: the imports listing's Search cell
 * (`ImportsTable`), Import Detail's Search card (`ImportInfoGrid`), the Company
 * Map card's "Original search" row (`BctRunDetail`), and the picker's rows plus
 * the collapsed selection bar — the last two rendered by BOTH Backchannel Tree
 * and AI Review. All of them answer "what happens when I click a search name?" the same
 * way. The listing and Import Detail were RETROFITTED onto this; each previously
 * rendered a single anchor to Thrive labelled `{id} - {name}` with no route to
 * Search Detail, so fixing only the picker would have left three spellings of one
 * concept.
 *
 * `newTab` DEFAULTS TO TRUE, and the default is chosen by what breaks when the next
 * caller forgets to pass it — not by today's caller census. The costs are
 * asymmetric: forgetting it on an in-app surface costs a full document load, which
 * is annoying; forgetting it where unsaved work lives DESTROYS that work. AI Review
 * is the sharpest case — it renders this without passing `newTab`, and it already
 * vetoes an import CHANGE when a full run is unapplied, calling that veto "the
 * data-loss guard". A search-name click is not covered by that modal, so an in-tab
 * default would open an unguarded exit out of the one page the codebase already
 * judged worth a dialog. BCT is the same shape with form fields instead of scores.
 *
 * So: the surfaces with nothing to lose opt OUT explicitly
 * (`newTab={false}` — `ImportsTable`, whose sibling `SearchesTable` already
 * navigates in-tab to this same page; `ImportInfoGrid`, which is where you land
 * FROM that row; and `BctRunDetail`, which sits on that same page and shows the
 * SAME search as the card above it, so a different navigation behaviour for the
 * same destination on one screen would be the inconsistency this component
 * exists to remove). An earlier version of this docblock said "do not 'fix' it into a
 * plain `<Link>`" — which is exactly what `newTab={false}` now does, deliberately,
 * on those three. The rule is not "never in-tab"; it is "in-tab only where the caller
 * has proven there is nothing to lose."
 *
 * KEEP THIS CENSUS CURRENT. It was stale for two `/pr-review` iterations after
 * `BctRunDetail` became the fifth caller and the third opt-out — a docblock that
 * counts its callers is a claim that expires whenever someone adds one, and it is
 * load-bearing here because the `newTab` default is argued FROM the census.
 *
 * PROPAGATION. Every anchor stops click and keydown, because `ImportsTable`
 * renders this inside a `<tr>` that navigates on click AND on Enter/Space (with
 * `preventDefault`). Without the keydown half, Enter on a focused link would
 * bubble to that row handler and navigate to the import instead of opening the
 * search. The picker no longer needs this — its anchors sit outside the
 * activation button — but the listing does, so the guard is live, not defensive.
 *
 * What the handlers must NOT do is call `preventDefault`: swallowing the default
 * is what stops the link opening at all. `ImportSearchLinks.test.tsx` asserts
 * `defaultPrevented` stays false and that assertion is mutation-tested. The
 * keydown guard is also gated to Enter/Space so it cannot swallow arrow keys if
 * roving-tabindex navigation is ever added around it.
 *
 * No `search_id_thrive` means there is nothing to link to: render the plain
 * "Unlinked" label, never an anchor with an empty target.
 *
 * `wrapSearchName` (2026-10-08, epic `2026-10-08-candidates-polish-thrive-avatar-worker`,
 * sub-task 02): the name is cut with an ellipsis by default, which is right on
 * the narrow surfaces (picker rows, the selection bar) but on the imports
 * listing a `truncate` in a cell with no width cap kept the name on ONE line
 * and widened the Search column until the Actions column left the screen.
 * Jose: there, the name WRAPS onto as many lines as it needs, never cut. Only
 * `ImportsTable` passes it, together with a width cap on the wrapper.
 */
import type React from "react";
import { Link } from "react-router-dom";

import { thriveSearchCandidatesUrl } from "../../../lib/thriveLinks";
import { searchDetailPath } from "../../../routing/paths";

export interface ImportSearchLinksProps {
  /** The linked Thrive search id, or `null` when the import is unlinked. */
  searchIdThrive: string | null;
  /** Cached search name, or `null` when it was never resolved. */
  searchName: string | null;
  /** Extra classes for the wrapper (lets callers own layout, not link style). */
  className?: string;
  /** Disambiguates the test ids when several instances share a screen. */
  testIdPrefix?: string;
  /**
   * Open the SEARCH DETAIL link in a new tab. Defaults to `true`, which is what
   * BCT needs: it holds unsaved form state that an in-tab SPA navigation would
   * destroy, so the safe value is the default and a caller has to opt out.
   *
   * Pass `false` on surfaces with nothing to lose — the imports listing and Import
   * Detail — where `_blank` on an in-app route costs a full document load and
   * diverges from `SearchesTable`, which navigates in-tab to the same page.
   *
   * The THRIVE link ignores this: it is always a new tab, because it leaves the
   * app entirely.
   */
  newTab?: boolean;
  /**
   * Wrap the search name onto as many lines as it needs instead of cutting it
   * with an ellipsis. Pair it with a width cap in `className`, or the name
   * simply stays on one long line. Default `false` (truncate).
   */
  wrapSearchName?: boolean;
}

/**
 * Keep a link click/keypress from reaching an enclosing selectable row.
 *
 * `stopPropagation` only. See the docblock: adding `preventDefault` here would
 * stop the link from opening at all.
 */
function stopRowActivation(e: React.MouseEvent | React.KeyboardEvent): void {
  // Only the keys an enclosing row treats as activation are stopped. Stopping
  // EVERY key would swallow arrows from any future roving-tabindex navigation —
  // which `ImportPicker` explicitly contemplates.
  //
  // Discriminated on `e.type` rather than `"key" in e`. Both work — a React
  // synthetic mouse event does NOT carry `key`, so the `in` form does not
  // misfire on clicks (measured: swapping the two leaves the suite green). `type`
  // is used because it states the intent instead of relying on which properties
  // React happens to put on a mouse event.
  if (e.type === "keydown") {
    const key = (e as React.KeyboardEvent).key;
    if (key !== "Enter" && key !== " ") return;
  }
  e.stopPropagation();
}

const LINK_CLASSES = "text-accent hover:text-accent-hover hover:underline";
const WRAPPER_CLASSES = "inline-flex min-w-0 items-center gap-1";
/** Baseline-aligned, so the id and the dot sit on the name's FIRST line. */
const WRAPPING_WRAPPER_CLASSES = "inline-flex min-w-0 items-baseline gap-1";

/**
 * Join the component's own classes with the caller's LAYOUT ones.
 *
 * Callers must not pass colour. The unlinked branch previously relied on
 * `text-text-muted` winning over a caller's `text-text-secondary`, which it did
 * only by an accident of token declaration order in the stylesheet — not by
 * specificity. Colour is owned here; `className` is for layout.
 */
function withCaller(base: string, caller?: string): string {
  return caller ? `${base} ${caller}` : base;
}

export function ImportSearchLinks({
  searchIdThrive,
  searchName,
  className,
  testIdPrefix = "import-search",
  newTab = true,
  wrapSearchName = false,
}: ImportSearchLinksProps) {
  // `min-w-0` lets the flex item shrink below its longest word, so
  // `break-words` can break a name that has no spaces at all.
  const nameClasses = wrapSearchName ? "min-w-0 break-words" : "truncate";
  if (!searchIdThrive) {
    return (
      <span
        className={withCaller("text-text-muted", className)}
        data-testid={`${testIdPrefix}-unlinked`}
      >
        Unlinked
      </span>
    );
  }

  return (
    <span
      className={withCaller(wrapSearchName ? WRAPPING_WRAPPER_CLASSES : WRAPPER_CLASSES, className)}
      data-testid={`${testIdPrefix}-links`}
    >
      <a
        href={thriveSearchCandidatesUrl(searchIdThrive)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={stopRowActivation}
        onKeyDown={stopRowActivation}
        title={`Open search ${searchIdThrive} in Thrive`}
        data-testid={`${testIdPrefix}-thrive-link`}
        className={`shrink-0 ${LINK_CLASSES}`}
      >
        {searchIdThrive}
      </a>
      {searchName ? (
        <>
          <span aria-hidden="true" className="shrink-0 text-text-muted">
            ·
          </span>
          {newTab ? (
            <a
              href={searchDetailPath(searchIdThrive)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={stopRowActivation}
              onKeyDown={stopRowActivation}
              title={`Open ${searchName} in nu-assist`}
              data-testid={`${testIdPrefix}-detail-link`}
              className={`${nameClasses} ${LINK_CLASSES}`}
            >
              {searchName}
            </a>
          ) : (
            <Link
              to={searchDetailPath(searchIdThrive)}
              onClick={stopRowActivation}
              onKeyDown={stopRowActivation}
              title={`Open ${searchName}`}
              data-testid={`${testIdPrefix}-detail-link`}
              className={`${nameClasses} ${LINK_CLASSES}`}
            >
              {searchName}
            </Link>
          )}
        </>
      ) : null}
    </span>
  );
}
